import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getLookbook, publishLookbook, republishLookbook } from '@/lib/lookbook-api';
import { fetchSubmissionThumbnailDataUrl } from '@/lib/submissions-api';
import { ArrowLeft, Globe, Link2, UserCheck, Lock, Users, Eye, ChevronRight } from 'lucide-react';

const POLICIES = [
  { key: 'PUBLIC', label: 'Public', sub: 'Anyone with the link.', icon: Globe },
  { key: 'PRIVATE_LINK', label: 'Private Link', sub: 'Only people with the exact link.', icon: Link2 },
  { key: 'INVITE_ONLY', label: 'Invite Only', sub: 'Signed-in guests with matching invitation.', icon: UserCheck },
  { key: 'PASSWORD', label: 'Password Protected', sub: 'Link + password challenge.', icon: Lock },
  { key: 'ATTENDEES_ONLY', label: 'Attendees Only', sub: 'Signed-in event members only.', icon: Users },
];

export default function HostLookbookLaunchPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [policy, setPolicy] = useState('PRIVATE_LINK');
  const [password, setPassword] = useState('');
  const [allowDownloads, setAllowDownloads] = useState(false);
  const [allowSharing, setAllowSharing] = useState(false);
  const [showNames, setShowNames] = useState(true);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const body = await getLookbook(eventId); setData(body);
      if (body.manifest?.state === 'LIVE') {
        setPolicy(body.manifest.access_policy); setAllowDownloads(body.manifest.allow_downloads);
        setAllowSharing(body.manifest.allow_sharing); setShowNames(body.manifest.show_contributor_names);
        setNotify(!!body.manifest.notify_attendees);
      }
      setError(null);
    } catch (e) { setError(e?.message || 'Failed to load.'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  useEffect(() => {
    if (!data) return;
    const ids = new Set();
    if (data.lookbook?.hero_submission_id) ids.add(data.lookbook.hero_submission_id);
    for (const s of data.lookbook?.sections || []) for (const id of s.item_ids) ids.add(id);
    (async () => { for (const id of ids) { if (thumbs[id]) continue; try { const url = await fetchSubmissionThumbnailDataUrl(id); if (url) setThumbs(p => ({ ...p, [id]: url })); } catch {} } })();
  }, [data]); // eslint-disable-line

  const isRepublish = data?.manifest?.state === 'LIVE' || data?.manifest?.state === 'PAUSED';
  const placedCount = data?.lookbook?.sections?.reduce((n, s) => n + s.item_ids.length, 0) ?? 0;

  async function onLaunch() {
    if (!eventId) return;
    if (policy === 'PASSWORD' && password.length < 4) { window.alert('Password mode needs min 4 chars.'); return; }
    setBusy(true);
    try {
      const body = { access_policy: policy, allow_downloads: allowDownloads, allow_sharing: allowSharing, show_contributor_names: showNames, notify_attendees: notify, password: policy === 'PASSWORD' ? password : undefined };
      const res = isRepublish ? await republishLookbook(eventId, body) : await publishLookbook(eventId, body);
      window.alert(`Share token (save this):\n\n${res.share_token}\n\nOpen /lookbook/${res.share_token} to view.`);
      navigate(`/host-lookbook-live?event_id=${encodeURIComponent(eventId)}`, { replace: true });
    } catch (e) { window.alert(e?.message || 'Launch failed.'); }
    finally { setBusy(false); }
  }

  const ToggleRow = ({ label, sub, value, onChange, testId }) => (
    <div className="flex items-center gap-3 py-3 border-b border-[#E3E8EF]">
      <div className="flex-1"><p className="font-bold text-[#0B1B46]">{label}</p><p className="text-xs text-[#5B6472] mt-0.5">{sub}</p></div>
      <button onClick={() => onChange(!value)} className={`w-10 h-6 rounded-full transition-colors ${value ? 'bg-[#146BFF]' : 'bg-[#E3E8EF]'}`} data-testid={testId}>
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="host-lookbook-launch-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="lookbook-launch-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-4">Launch Lookbook</h1>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="lookbook-launch-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="lookbook-launch-error">{error}</div>
      ) : data ? (
        <div className="flex flex-col gap-4">
          {/* Event summary */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-launch-event-card">
            <div className="grid grid-cols-2 gap-1 mb-3">
              {data.lookbook.sections.flatMap(s => s.item_ids).slice(0, 4).map(id => (
                <div key={id} className="aspect-[1.35] rounded-md overflow-hidden bg-[#F3F5F8]" data-testid={`lookbook-launch-collage-${id}`}>
                  {thumbs[id] ? <img src={thumbs[id]} alt="" className="w-full h-full object-cover" /> : null}
                </div>
              ))}
            </div>
            <h2 className="text-lg font-extrabold text-[#0B1B46]" data-testid="lookbook-launch-event-name">{data.event?.name}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{placedCount} views placed</span>
              <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{data.lookbook.sections.length} sections</span>
            </div>
            <button onClick={() => navigate(`/host-lookbook-preview?event_id=${encodeURIComponent(eventId)}`)}
              className="w-full flex items-center gap-2 mt-3 px-3 py-2 rounded-md bg-[#EFF5FF] text-[#146BFF] font-bold text-sm hover:bg-[#D6E4FF] transition-colors"
              data-testid="lookbook-launch-preview-row"><Eye className="w-4 h-4" /><span className="flex-1 text-left">Preview Lookbook</span><ChevronRight className="w-4 h-4" /></button>
          </div>

          {/* Visibility */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-launch-visibility-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Visibility</p>
            <div className="flex flex-col gap-2">
              {POLICIES.map(p => {
                const sel = policy === p.key;
                return (
                  <button key={p.key} onClick={() => setPolicy(p.key)}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${sel ? 'border-[#146BFF] bg-[#EFF5FF]' : 'border-[#E3E8EF] bg-white hover:bg-[#F3F5F8]'}`}
                    data-testid={`lookbook-launch-policy-${p.key}`}>
                    <p.icon className={`w-5 h-5 mt-0.5 ${sel ? 'text-[#146BFF]' : 'text-[#5B6472]'}`} />
                    <div className="flex-1"><p className={`font-bold ${sel ? 'text-[#146BFF]' : 'text-[#0B1B46]'}`}>{p.label}</p><p className="text-xs text-[#5B6472] mt-0.5">{p.sub}</p></div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${sel ? 'border-[#146BFF]' : 'border-[#E3E8EF]'}`}>
                      {sel && <div className="w-2.5 h-2.5 rounded-full bg-[#146BFF]" />}
                    </div>
                  </button>
                );
              })}
            </div>
            {policy === 'PASSWORD' && (
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Set a password (min 4 chars)"
                className="w-full h-10 mt-3 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="lookbook-launch-password" />
            )}
          </div>

          {/* Permissions */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="lookbook-launch-permissions-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Viewer permissions</p>
            <ToggleRow label="Allow Downloads" sub="Viewers can download EXIF-stripped copies." value={allowDownloads} onChange={setAllowDownloads} testId="lookbook-launch-toggle-downloads" />
            <ToggleRow label="Allow Sharing" sub="Show a native share button." value={allowSharing} onChange={setAllowSharing} testId="lookbook-launch-toggle-sharing" />
            <ToggleRow label="Show Contributor Names" sub="Photo captions show who took each shot." value={showNames} onChange={setShowNames} testId="lookbook-launch-toggle-names" />
            <ToggleRow label="Notify Attendees" sub="In-app notification when lookbook goes live." value={notify} onChange={setNotify} testId="lookbook-launch-toggle-notify" />
          </div>

          <button onClick={onLaunch} disabled={busy || placedCount === 0}
            className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="lookbook-launch-cta">{busy ? 'Launching…' : isRepublish ? `Republish Lookbook (v${(data.manifest?.version ?? 0) + 1})` : 'Launch Lookbook'}</button>
          {placedCount === 0 && <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-lg p-3 text-sm text-[#5B6472]">Place at least one view before launching.</div>}
        </div>
      ) : null}
    </div>
  );
}
